import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { MapPin, Heart, Waves, Scissors } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.6 },
  }),
};

export default function AboutPage() {
  return (
    <div className="pt-28 lg:pt-44" data-testid="about-page">
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial="hidden" animate="visible">
              <motion.p variants={fadeUp} custom={0} className="font-accent text-2xl text-sunset mb-4">
                hey, I'm Jax
              </motion.p>
              <motion.h1
                variants={fadeUp}
                custom={1}
                className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-8"
              >
                Living My Best Life in Cornwall
              </motion.h1>
              <motion.div variants={fadeUp} custom={2} className="space-y-4 text-charcoal leading-relaxed">
                <p>
                  I'm a hairstylist by trade, a surf and sea enthusiast, a coffee lover and always
                  striving for peace and happiness.
                </p>
                <p>
                  It became obvious to me that several of my followers started following me to watch
                  my happiness grow, being inspired and affected by my energy. So now I'm expanding
                  'Me' as my brand into something that can inspire others to find their place and
                  happiness within themselves and in their lives.
                </p>
                <p>
                  I specialise in curly and wavy hair, creating natural-looking styles that make
                  people feel like the best version of themselves even when styling at home. I'm also
                  trained in Indian head massage which allows me to bridge the gap between 'salon'
                  and 'wellness'.
                </p>
                <p>
                  And that's why Justjjax exists. It's a collective for more than just hair — it's
                  wellness, community, health, knowledge, education, surfing and networking and of
                  course, don't forget the good vibes!
                </p>
                <p>
                  Beyond the salon chair, I'm building a community. Through workshops, beach wellness
                  mornings and events that bring entrepreneurs together, I want to share the good
                  energy.
                </p>
              </motion.div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="relative"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_high-on-life-1/artifacts/ziiwtjpv_83AA661D-CB03-485C-81B6-5F4C71379F91-308-00000012CDE52B59.jpg"
                alt="Jax on the Cornwall coast"
                className="rounded-3xl w-full aspect-[3/4] object-cover object-top"
              />
              <div className="absolute -bottom-6 -left-6 bg-olive text-white rounded-2xl p-6 shadow-lg">
                <p className="font-accent text-lg">Cornwall based</p>
                <p className="text-sm text-white/80 flex items-center gap-1">
                  <MapPin size={14} /> Bude
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-seafoam">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            custom={0}
            className="font-display text-3xl sm:text-4xl tracking-tight text-center mb-16"
          >
            What Drives Me
          </motion.h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Scissors,
                title: "Creative Craft",
                desc: "Hair is my art. Every person who sits in my chair gets a bespoke experience that honours their natural beauty.",
                bg: "bg-peachlight",
              },
              {
                icon: Heart,
                title: "Genuine Wellness",
                desc: "From Indian head massage to sea swimming, I believe in holistic practices that nourish mind, body and soul.",
                bg: "bg-warmcream",
              },
              {
                icon: Waves,
                title: "Community Spirit",
                desc: "Cornwall's energy inspires everything I do. I'm building spaces where people connect, create and grow together.",
                bg: "bg-rose",
              },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={i}
                className={`${item.bg} rounded-3xl p-8 border border-ocean/5`}
              >
                <item.icon className="text-sunset mb-4" size={28} />
                <h3 className="font-display text-xl mb-3">{item.title}</h3>
                <p className="text-charcoal/60 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="font-accent text-xl text-sunset mb-4">let's connect</p>
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight mb-8">
            Ready to Work Together?
          </h2>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="https://tinyurl.com/2nxodcgu"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-olive text-white hover:bg-amber rounded-full px-8 py-3.5 font-medium transition-all duration-300"
              data-testid="about-book-btn"
            >
              Book a Session
            </a>
            <Link
              to="/work-with-me"
              className="bg-transparent border border-ocean text-black hover:bg-eucalyptus hover:text-white rounded-full px-8 py-3.5 font-medium transition-all duration-300"
              data-testid="about-work-btn"
            >
              Work With Me
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
